import React, { useState, useEffect } from "react";
import "./styles.scss";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { hasEnPrefix, toLangPath } from "../../utilities/helpers";
import { getFilesList } from "../../actions/resources";
import { OfferBanner } from "../../components";
import { Trans, useTranslation } from "react-i18next";
import SEO from "../../components/SEO/SEO";

const documentsIcon = require("../../assets/icons/documents.png");

const SERVICE_CARDS = [
  {
    id: "first",
    icon: require("../../assets/icons/services-icon-1.svg").default,
    title: "cardOneTitle",
    text: "cardOneText",
    bullets: ["cardOneHoveredTextOne", "cardOneHoveredTextTwo", "cardOneHoveredTextThree"],
  },
  {
    id: "second",
    icon: require("../../assets/icons/services-icon-2.svg").default,
    title: "cardTwoTitle",
    text: "cardTwoText",
    bullets: ["cardTwoHoveredTextOne", "cardTwoHoveredTextTwo", "cardTwoHoveredTextThree"],
  },
  {
    id: "third",
    icon: require("../../assets/icons/services-icon-3.svg").default,
    title: "cardThreeTitle",
    text: "cardThreeText",
    bullets: ["cardThreeHoveredTextOne", "cardThreeHoveredTextTwo", "cardThreeHoveredTextThree"],
  },
  {
    id: "fourth",
    icon: documentsIcon,
    iconClass: "documents",
    title: "cardFourTitle",
    text: "cardFourText",
    bullets: ["cardFourHoveredTextOne", "cardFourHoveredTextTwo", "cardFourHoveredTextThree", "cardFourHoveredTextFour"],
    showDocuments: true,
  },
];

const APPLICATION_KEYS = [
  "заявление за предоставяне на допълнителни услуги",
  "application for additional services",
];
const PRICE_LIST_KEYS = [
  "ценоразпис на допълнителните услуги",
  "price list of additional services",
];

const findFileUrl = (files = [], needles = [], lang = "bg") => {
  const keys = needles.map((key) => key.toLowerCase());
  const matches = files.filter((file) => {
    const titles = [file.title, file.titleEnglish].filter(Boolean).map((title) => title.toLowerCase());
    return keys.some((key) => titles.some((title) => title.includes(key)));
  });
  if (!matches.length) return "";

  const preferred = matches.find((file) => {
    const title = (lang === "en" ? file.titleEnglish : file.title) || "";
    return keys.some((key) => title.toLowerCase().includes(key));
  });

  return (preferred || matches[0]).url || "";
};

const BulletLine = ({ i18nKey }) => (
  <div className="flex start">
    <div className="line orange" />
    <h5>
      <Trans i18nKey={`services.${i18nKey}`} components={{ bold: <strong /> }} />
    </h5>
  </div>
);

const DocumentButtons = ({ applicationUrl, priceListUrl, t }) => {
  if (!applicationUrl && !priceListUrl) return null;

  return (
    <div className="document-buttons">
      {applicationUrl && (
        <a className="document-bttn" href={applicationUrl} target="_blank" rel="noreferrer">
          {t("services.downloadApplication")}
        </a>
      )}
      {priceListUrl && (
        <a className="document-bttn" href={priceListUrl} target="_blank" rel="noreferrer">
          {t("services.viewPriceList")}
        </a>
      )}
    </div>
  );
};

export default function Services() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const language = useSelector((state) => state.general.language);
  const [hoveredCard, setHoveredCard] = useState("");
  const [applicationUrl, setApplicationUrl] = useState("");
  const [priceListUrl, setPriceListUrl] = useState("");
  const { t } = useTranslation();

  const goToContact = () => {
    const lang = hasEnPrefix(window.location.pathname) ? "en" : "bg";
    navigate(toLangPath("/contact", lang));
  };

  useEffect(() => {
    window.scrollTo(0, 0, { behavour: "smooth" });
    dispatch(
      getFilesList({
        onSuccess: (files) => {
          setApplicationUrl(findFileUrl(files, APPLICATION_KEYS, language));
          setPriceListUrl(findFileUrl(files, PRICE_LIST_KEYS, language));
        },
      })
    );
  }, [dispatch, language]);

  return (
    <>
      <SEO
        title={`${t("productsAndServices.servicesTitle")} | Synergon Energy`}
        name={`${t("productsAndServices.servicesTitle")} | Synergon Energy`}
        description={t("services.servicesSubtitle")}
        type={"website"}
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Additional Services",
          name: "Synergon Energy Additional Services",
          description: t("services.servicesText"),
          provider: { "@type": "Organization", name: "Synergon Energy" },
          url: typeof window !== "undefined" ? window.location.origin + window.location.pathname : "https://synergonenergy.bg/additional-services"
        }]}
      />
      <div className="screen-wrapper services">
        <div className="screen-inner-wrapper">
          <div className="screen-header-wrapper">
            <div className="flex padding" onClick={() => navigate(-1)}>
              <img src={require("../../assets/icons/arrow.svg").default} alt="arrow back" />
              <span>{t("general.back")}</span>
            </div>

            <div className="section-wrapper text">
              <div className="title-wrapper">
                <h1>{t("productsAndServices.servicesTitle")}</h1>
                <div className="flex">
                  <div className="line orange" />
                  <h2>{t("services.servicesSubtitle")}</h2>
                </div>
                <p>{t("services.servicesText")}</p>
              </div>
            </div>
          </div>

          <div className="screen-main-wrapper desktop">
            <div className="cards-wrapper">
              {SERVICE_CARDS.map((card) => {
                const isHovered = hoveredCard === card.id;
                return (
                  <div
                    key={card.id}
                    className={`section-wrapper card ${isHovered ? "hidden" : ""}`}
                    onMouseOver={() => setHoveredCard(card.id)}
                    onMouseLeave={() => setHoveredCard("")}
                  >
                    <div className="section-inner-wrapper">
                      <img className={`icon ${card.iconClass || ""}`} src={card.icon} alt="" />
                      <h2>{t(`services.${card.title}`)}</h2>
                      <p>{t(`services.${card.text}`)}</p>
                    </div>

                    <div className={`flex col ${isHovered ? "hovered" : "hidden"}`}>
                      {card.bullets.map((bullet) => (
                        <BulletLine key={bullet} i18nKey={bullet} />
                      ))}
                      {card.showDocuments ? (
                        <DocumentButtons applicationUrl={applicationUrl} priceListUrl={priceListUrl} t={t} />
                      ) : (
                        <button className="primary-bttn" onClick={goToContact}>
                          {t("general.contactUs")}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="screen-main-wrapper mobile">
            <div className="cards-wrapper">
              {SERVICE_CARDS.map((card) => (
                <div className="section-wrapper card" key={card.id}>
                  <div className="section-inner-wrapper">
                    <img className={`icon ${card.iconClass || ""}`} src={card.icon} alt="" />
                    <h2>{t(`services.${card.title}`)}</h2>
                    <p>{t(`services.${card.text}`)}</p>
                  </div>

                  <div className="flex col">
                    {card.bullets.map((bullet) => (
                      <BulletLine key={bullet} i18nKey={bullet} />
                    ))}
                  </div>

                  {card.showDocuments ? (
                    <DocumentButtons applicationUrl={applicationUrl} priceListUrl={priceListUrl} t={t} />
                  ) : (
                    <button className="primary-bttn" onClick={goToContact}>
                      {t("general.contactUs")}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <OfferBanner />
        </div>
      </div>
    </>
  );
}
